import React from 'react'
import { Check, ChevronRight } from 'lucide-react'

interface OpportunityStageProgressProps {
  currentStage: string
  onStageChange: (newStage: string) => void
  disabled?: boolean
}

type OpportunityStage =
  | 'New'
  | 'Need Identified'
  | 'Presentation Complete'
  | 'Initial Quote Complete'
  | 'Final Quote'
  | 'Verbal Received'
  | 'Closed Won'
  | 'Closed Lost'
  | 'Service (Do not Use)'

// Main progression stages (excluding final stages and service)
const MAIN_STAGES: OpportunityStage[] = [
  'New',
  'Need Identified',
  'Presentation Complete',
  'Initial Quote Complete',
  'Final Quote',
  'Verbal Received',
]

// Final stages that branch
const FINAL_STAGES: OpportunityStage[] = ['Closed Won', 'Closed Lost']

// Other stages
const OTHER_STAGES: OpportunityStage[] = ['Service (Do not Use)']

export function OpportunityStageProgress({
  currentStage,
  onStageChange,
  disabled = false,
}: OpportunityStageProgressProps) {
  const normalizedCurrentStage = currentStage as OpportunityStage
  const currentMainIndex = MAIN_STAGES.indexOf(normalizedCurrentStage)
  const isFinalStage = FINAL_STAGES.includes(normalizedCurrentStage)
  const isOtherStage = OTHER_STAGES.includes(normalizedCurrentStage)

  const isStageCompleted = (stage: OpportunityStage): boolean => {
    const stageIndex = MAIN_STAGES.indexOf(stage)
    if (stageIndex === -1) return false
    return currentMainIndex > stageIndex || isFinalStage
  }

  const isStageActive = (stage: OpportunityStage): boolean => {
    return stage === normalizedCurrentStage
  }

  const canProgressToStage = (stage: OpportunityStage): boolean => {
    if (disabled) return false

    // Can't click on the current stage
    if (stage === normalizedCurrentStage) return false

    // Can move between any stages
    return true
  }

  const handleStageClick = (stage: OpportunityStage) => {
    if (canProgressToStage(stage)) {
      onStageChange(stage)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, stage: OpportunityStage) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleStageClick(stage)
    }
  }

  const getStageStyles = (stage: OpportunityStage): string => {
    if (isStageActive(stage)) {
      if (stage === 'Closed Won') {
        return 'bg-accent/10 text-accent border-accent ring-2 ring-accent'
      } else if (stage === 'Closed Lost') {
        return 'bg-destructive/10 text-destructive border-destructive ring-2 ring-destructive'
      } else {
        return 'bg-primary/10 text-primary border-primary ring-2 ring-primary'
      }
    }

    if (isStageCompleted(stage)) {
      return 'bg-gray-100 text-gray-600 border-gray-300'
    }

    if (canProgressToStage(stage)) {
      if (stage === 'Closed Won') {
        return 'bg-white text-accent border-accent/30 hover:bg-accent/5 cursor-pointer'
      } else if (stage === 'Closed Lost') {
        return 'bg-white text-destructive border-destructive/30 hover:bg-destructive/5 cursor-pointer'
      } else {
        return 'bg-white text-primary border-primary/30 hover:bg-primary/5 cursor-pointer'
      }
    }

    return 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
  }

  const renderStageButton = (stage: OpportunityStage, showArrow: boolean = true) => {
    const isActive = isStageActive(stage)
    const isCompleted = isStageCompleted(stage)
    const canProgress = canProgressToStage(stage)

    return (
      <React.Fragment key={stage}>
        <button
          onClick={() => handleStageClick(stage)}
          onKeyDown={(e) => handleKeyDown(e, stage)}
          disabled={!canProgress}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-all
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
            ${getStageStyles(stage)}
          `}
          aria-label={`${stage} stage${isActive ? ' (current)' : ''}${canProgress ? ' - click to change' : ''}`}
          aria-current={isActive ? 'step' : undefined}
          tabIndex={canProgress ? 0 : -1}
        >
          {isCompleted && !isActive && <Check className="w-4 h-4" aria-hidden="true" />}
          <span className="font-medium">{stage}</span>
        </button>
        {showArrow && (
          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
        )}
      </React.Fragment>
    )
  }

  return (
    <nav
      className="bg-white border rounded-lg p-4 shadow-sm"
      aria-label="Opportunity stage progression"
      role="navigation"
    >
      <div className="flex items-center justify-start gap-3 flex-wrap">
        {/* Main progression */}
        {MAIN_STAGES.map((stage, index) =>
          renderStageButton(stage, index < MAIN_STAGES.length - 1)
        )}

        {/* Final stages branching */}
        {(isFinalStage ||
          currentMainIndex === MAIN_STAGES.length - 1 ||
          currentMainIndex === -1) && (
          <>
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <div className="flex items-center gap-3">
              {renderStageButton('Closed Won', false)}
              <span className="text-gray-400 font-medium" aria-hidden="true">
                /
              </span>
              {renderStageButton('Closed Lost', false)}
            </div>
          </>
        )}

        {/* Other stages (Service) - show if current or show all option */}
        {(isOtherStage || currentMainIndex === -1) && (
          <>
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <div className="flex items-center gap-3">
              {OTHER_STAGES.map((stage) => renderStageButton(stage, false))}
            </div>
          </>
        )}
      </div>

      {/* Screen reader only text for context */}
      <div className="sr-only">
        Opportunity stage progression: New through Verbal Received, then either Closed Won or Closed
        Lost. Service stage also available. Current stage: {currentStage}. You can move between any
        stages. Use Tab to navigate and Enter to change stages.
      </div>
    </nav>
  )
}
